import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './Navbar';

const MediaSynthesisService = () => {
  const navigate = useNavigate();
  const [selectedFeature, setSelectedFeature] = useState('overview');

  const features = [
    {
      id: 'video',
      title: 'AI Video Generation',
      icon: 'videocam',
      description: 'Create professional videos from text prompts, scripts, or existing content with advanced AI models.',
      benefits: ['Generate videos in minutes', 'Multiple styles & formats', 'Custom branding', 'Batch processing'],
      metrics: { speed: '10x faster', quality: '4K resolution', formats: '50+ formats' }
    },
    {
      id: 'audio',
      title: 'Voice & Audio Synthesis',
      icon: 'mic',
      description: 'Generate realistic voiceovers, music, and sound effects using cutting-edge AI audio models.',
      benefits: ['Natural voice cloning', 'Multi-language support', 'Custom music generation', 'Real-time processing'],
      metrics: { languages: '40+ languages', quality: 'Studio grade', speed: 'Real-time' }
    },
    {
      id: 'image',
      title: 'Visual Content Creation',
      icon: 'image',
      description: 'Generate stunning images, graphics, and visual assets for any creative project or campaign.',
      benefits: ['Unlimited variations', 'Brand consistency', 'High-resolution output', 'Style transfer'],
      metrics: { resolution: 'Up to 8K', styles: '100+ styles', generation: '< 30 seconds' }
    },
    {
      id: 'editing',
      title: 'Automated Editing',
      icon: 'movie_edit',
      description: 'Intelligent video and audio editing with automated cuts, transitions, and post-production effects.',
      benefits: ['Smart scene detection', 'Auto color correction', 'Dynamic transitions', 'Batch editing'],
      metrics: { accuracy: '95%', speed: '20x faster', formats: 'All major formats' }
    }
  ];

  const useCases = [
    {
      title: 'Marketing & Advertising',
      description: 'Create compelling ad campaigns, social media content, and promotional videos at scale.',
      icon: 'campaign',
      industries: ['Digital Marketing', 'Advertising Agencies', 'Social Media']
    },
    {
      title: 'Entertainment & Media',
      description: 'Produce content for streaming, gaming, and entertainment platforms with AI assistance.',
      icon: 'movie',
      industries: ['Film Production', 'Gaming', 'Streaming Platforms']
    },
    {
      title: 'Education & Training',
      description: 'Develop educational content, training materials, and interactive learning experiences.',
      icon: 'school',
      industries: ['E-learning', 'Corporate Training', 'Educational Institutions']
    },
    {
      title: 'E-commerce & Retail',
      description: 'Generate product videos, demonstrations, and marketing content for online stores.',
      icon: 'shopping_cart',
      industries: ['E-commerce', 'Product Marketing', 'Retail Brands']
    }
  ];

  const capabilities = [
    {
      title: 'Text-to-Video',
      description: 'Transform written content into engaging video presentations',
      icon: 'text_fields',
      color: 'from-blue-500 to-purple-500'
    },
    {
      title: 'Voice Cloning',
      description: 'Create custom voices that match your brand personality',
      icon: 'record_voice_over',
      color: 'from-green-500 to-teal-500'
    },
    {
      title: 'Style Transfer',
      description: 'Apply artistic styles and visual effects to any content',
      icon: 'palette',
      color: 'from-pink-500 to-rose-500'
    },
    {
      title: 'Real-time Processing',
      description: 'Generate and edit content with minimal latency',
      icon: 'speed',
      color: 'from-orange-500 to-red-500'
    }
  ];

  const integrations = [
    { name: 'Adobe Creative Suite', icon: 'design_services', category: 'Design Tools' },
    { name: 'YouTube', icon: 'video_library', category: 'Video Platforms' },
    { name: 'Spotify', icon: 'library_music', category: 'Audio Platforms' },
    { name: 'Figma', icon: 'draw', category: 'Design Collaboration' },
    { name: 'Slack', icon: 'chat', category: 'Team Communication' },
    { name: 'Dropbox', icon: 'cloud_upload', category: 'File Storage' }
  ];

  return (
    <div className="min-h-screen bg-dark">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-purple-500/10 via-transparent to-pink-500/10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl flex items-center justify-center">
                <span className="material-symbols-outlined text-purple-400 text-3xl">movie_edit</span>
              </div>
              <div className="text-left">
                <h1 className="text-4xl md:text-6xl font-bold text-white">
                  Media <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">Synthesis</span>
                </h1>
                <p className="text-gray-400 text-lg mt-2">AI-Powered Content Creation Platform</p>
              </div>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto mb-8">
              Revolutionize your content creation workflow with advanced AI that generates videos, audio, 
              images, and complete multimedia experiences from simple text prompts or existing assets.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105">
                Try Free Demo
              </button>
              <button className="border border-gray-600 hover:border-purple-500 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all">
                View Examples
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-16 bg-gray-900/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Core Capabilities</h2>
            <p className="text-gray-300 text-lg">Powerful AI tools for every aspect of content creation</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {capabilities.map((capability, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-purple-500/50 transition-all group">
                <div className={`w-12 h-12 bg-gradient-to-r ${capability.color} rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}>
                  <span className="material-symbols-outlined text-white text-2xl">{capability.icon}</span>
                </div>
                <h3 className="text-lg font-bold text-white mb-2">{capability.title}</h3>
                <p className="text-gray-300 text-sm">{capability.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Navigation */}
      <section className="py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {features.map((feature) => (
              <button
                key={feature.id}
                onClick={() => setSelectedFeature(feature.id)}
                className={`flex items-center gap-3 px-6 py-3 rounded-lg transition-all ${
                  selectedFeature === feature.id
                    ? 'bg-gradient-to-r from-purple-500 to-pink-500 text-white shadow-lg'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                <span className="material-symbols-outlined">{feature.icon}</span>
                <span className="font-medium">{feature.title}</span>
              </button>
            ))}
          </div>

          {/* Feature Details */}
          {features.map((feature) => (
            selectedFeature === feature.id && (
              <div key={feature.id} className="bg-gray-800 rounded-2xl p-8 border border-gray-700">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                        <span className="material-symbols-outlined text-purple-400 text-2xl">{feature.icon}</span>
                      </div>
                      <h3 className="text-2xl font-bold text-white">{feature.title}</h3>
                    </div>
                    <p className="text-gray-300 text-lg mb-6">{feature.description}</p>
                    <div className="space-y-3">
                      <h4 className="text-white font-semibold">Key Features:</h4>
                      <ul className="space-y-2">
                        {feature.benefits.map((benefit, index) => (
                          <li key={index} className="flex items-center gap-3 text-gray-300">
                            <span className="w-2 h-2 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full"></span>
                            {benefit}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                  <div className="bg-gray-900 rounded-xl p-6">
                    <h4 className="text-white font-semibold mb-4">Technical Specs</h4>
                    <div className="grid grid-cols-1 gap-4">
                      {Object.entries(feature.metrics).map(([key, value]) => (
                        <div key={key} className="flex justify-between items-center p-3 bg-gray-800 rounded-lg">
                          <span className="text-gray-400 capitalize">{key}</span>
                          <span className="text-purple-400 font-bold">{value}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )
          ))}
        </div>
      </section>

      {/* Use Cases */}
      <section className="py-16 bg-gray-900/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Industry Applications</h2>
            <p className="text-gray-300 text-lg">Transforming content creation across industries</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {useCases.map((useCase, index) => (
              <div key={index} className="bg-gray-800 rounded-xl p-6 border border-gray-700 hover:border-purple-500/50 transition-all">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-lg flex items-center justify-center">
                    <span className="material-symbols-outlined text-purple-400 text-2xl">{useCase.icon}</span>
                  </div>
                  <h3 className="text-xl font-bold text-white">{useCase.title}</h3>
                </div>
                <p className="text-gray-300 mb-4">{useCase.description}</p>
                <div className="flex flex-wrap gap-2">
                  {useCase.industries.map((industry, idx) => (
                    <span key={idx} className="bg-gradient-to-r from-purple-500/10 to-pink-500/10 text-purple-400 px-3 py-1 rounded-full text-sm border border-purple-500/20">
                      {industry}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Integrations */}
      <section className="py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-white mb-4">Creative Tool Integrations</h2>
            <p className="text-gray-300 text-lg">Seamlessly integrate with your existing creative workflow</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
            {integrations.map((integration, index) => (
              <div key={index} className="bg-gray-800 rounded-lg p-4 text-center hover:bg-gray-700 transition-all group">
                <div className="w-12 h-12 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-3 group-hover:bg-gradient-to-r group-hover:from-purple-500/20 group-hover:to-pink-500/20 transition-all">
                  <span className="material-symbols-outlined text-purple-400">{integration.icon}</span>
                </div>
                <h4 className="text-white font-medium text-sm mb-1">{integration.name}</h4>
                <p className="text-gray-400 text-xs">{integration.category}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-purple-500/10 to-pink-500/10">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">Create Without Limits</h2>
          <p className="text-xl text-gray-300 mb-8">
            Join the next generation of creators using AI to produce stunning content at unprecedented speed and scale.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button 
              onClick={() => navigate('/signup')}
              className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all transform hover:scale-105"
            >
              Start Creating Now
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="border border-purple-500 hover:bg-gradient-to-r hover:from-purple-500 hover:to-pink-500 text-purple-400 hover:text-white px-8 py-4 rounded-lg font-semibold text-lg transition-all"
            >
              Request Demo
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default MediaSynthesisService;